import { createImage } from '@liveauctioneers/hammer-ui-core/card/components/createImage';
import { createInfo } from '@liveauctioneers/hammer-ui-core/card/components/createInfo';
import { cardFactory } from '@liveauctioneers/hammer-ui-core/card/factory';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { usePreventDefaultLinkOnClick } from '@liveauctioneers/hammer-ui-core/providers';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { CSSProperties } from 'react';

import { ArticleSummary } from '@/redux/modules/article/article.types';
import {
    ArticleClickedTrigger,
    useTrackArticleClickedAnalytics,
} from '@/utils/amplitude/analytics/article/trackArticleClicked.analytics';
import { formatArticleDate } from '@/utils/dates';
import { getGuideArticleUrl } from '@/utils/urls';

import { ArticleLinkCardImage } from './components/ArticleLinkCardImage';

export const ARTICLE_LINK_CARD_WIDTH_CLASS = 'w-[240px] smMin:w-[381px]';

type ArticleLinkCardData = ArticleSummary & {
    position?: number;
};

const ArticleLinkCardImageLink = ({
    className,
    position = 0,
    style,
    ...article
}: ArticleLinkCardData & { className: string; style?: CSSProperties }) => {
    const href = getGuideArticleUrl(article.topic, article.slug);
    const navigate = usePreventDefaultLinkOnClick();
    const trackArticleClicked = useTrackArticleClickedAnalytics();

    return (
        <a
            className="peer/card-image relative w-full cursor-pointer"
            href={href}
            onClick={(event) => {
                trackArticleClicked({
                    articleId: article.id,
                    position: position + 1,
                    trigger: ArticleClickedTrigger.ImageClick,
                });
                navigate(href)(event);
            }}
            style={style}
            tabIndex={-1}
        >
            <ArticleLinkCardImage
                className={className}
                heroImageUrl={article.heroImageUrl}
                secondaryImageUrl={article.secondaryImageUrl}
                title={article.title}
            />
        </a>
    );
};

export const ArticleLinkCard = cardFactory<ArticleLinkCardData>({
    components: [
        createImage<ArticleLinkCardData>({
            aspectRatio: 2,
            ImageComponent: ArticleLinkCardImageLink,
        }),
        { spacerPx: 8 },
        createInfo<ArticleLinkCardData>({
            className: 'line-clamp-0',
            useText: ({ id, position = 0, slug, title, topic }) => {
                const trackArticleClicked = useTrackArticleClickedAnalytics();
                const href = getGuideArticleUrl(topic, slug);

                return (
                    <SubtleMonochromeLink
                        className="hui-card-name line-clamp-2 font-semibold text-text-primary peer-hover/card-image:text-link-primary peer-hover/card-image:underline"
                        href={href}
                        onClick={() => {
                            trackArticleClicked({
                                articleId: id,
                                position: position + 1,
                                trigger: ArticleClickedTrigger.TitleClick,
                            });
                        }}
                    >
                        {title}
                    </SubtleMonochromeLink>
                );
            },
        }),
        { spacerPx: 4 },
        createInfo<ArticleSummary>({
            className: 'line-clamp-0',
            useText: ({ author, createdAt }) => {
                const formattedDate = formatArticleDate(createdAt);

                if (!author && !formattedDate) {
                    return null;
                }

                return (
                    <div className="flex flex-col gap-2xs">
                        {author && <BodySecondary color="secondary">By {author}</BodySecondary>}
                        {formattedDate && <BodySecondary color="secondary">{formattedDate}</BodySecondary>}
                    </div>
                );
            },
        }),
    ],
    name: 'ArticleLinkCard',
});
